import { captureEvent } from './posthog';

// Eventos de domínio do admin (admin.etiquetachef.com). Foco em
// ações administrativas: super-admin login, faturas e configuração de
// billing (Sprint 16), uso dos painéis de BI e push. Eventos de cliente final
// (etiquetas, onboarding) ficam no apps/app.

/**
 * Catálogo dos eventos PostHog emitidos pelo admin.
 *
 * Origem: sem issue — commit 2e96ce7ba, 2026-04-28 (Sprint 9 PR C, eventos de
 * domínio + funnels).
 *
 * Contrato: cada método chama `captureEvent` (`lib/posthog.ts`) com nome fixo
 * e payload pequeno; nunca lança (o PostHog é fire-and-forget). Textos livres
 * (`reason`) são cortados em `MAX_REASON_LEN` para não levar dado pessoal
 * longo nem estourar a propriedade.
 *
 * Usado por: `auth/AuthProvider.tsx` (login/logout), `lib/firestoreTenants.ts`
 * (`tenantDocDropped`), `lib/useWebPush.ts` e `lib/webPushController.ts`
 * (push), `features/shared/ExportButton.tsx`, `features/reports/ReportsPage.tsx`,
 * `features/dashboard-alerts/AcknowledgeButton.tsx`,
 * `features/admin-usage/AdminUsageDashboardPage.tsx`,
 * `components/OpenInPostHogButton.tsx` e os diálogos de `features/billing/`.
 *
 * Dados: evento PostHog `{ event: '<nome>', properties: { ... } }`. Ex.:
 *   { "event": "tenant_doc_dropped",
 *     "properties": { "tenantId": "tenant-1", "fields": "status,plan", "severity": "WARN" } }
 * `tenantId` é o id do documento (não é dado pessoal); nunca mandar e-mail,
 * telefone ou CNPJ aqui.
 *
 * Dicas: o nome do evento é contrato com os dashboards e funis do PostHog —
 * renomear quebra o histórico. Eventos do cliente final ficam no `apps/app`.
 * Todo nome daqui está no catálogo `packages/types/src/analyticsCatalog.ts`
 * com a superfície `admin` (#939), e o `analyticsCatalog.test.ts` cobra os
 * dois sentidos e o chamador de cada método. Nunca use a chave `surface` numa
 * prop: ela é a super property que separa o admin das outras superfícies.
 *
 * Histórico:
 *  - 2026-10-13 18:23 +0200 — (#939) saíram os 7 métodos sem chamador
 *    (`tenantSuspended`/`tenantReactivated`, `planPriceUpdated`,
 *    `billingCycleChanged` — que repetia o nome do evento do servidor com
 *    outras props —, `biDashboardViewed`, `biDrilldownOpened`,
 *    `biFilterApplied`); `biPostHogDeeplinkOpened` manda `dashboard`.
 */
export const trackEvents = {
  login(): void {
    captureEvent('login', { provider: 'google-workspace' });
  },
  logout(): void {
    captureEvent('logout');
  },

  // Doc de tenant recusado pelo Zod e omitido da listagem — sinaliza
  // legado/corrupção que sumiria silenciosamente sem este report.
  tenantDocDropped(tenantId: string, fields: string): void {
    captureEvent('tenant_doc_dropped', { tenantId, fields, severity: 'WARN' });
  },

  // Billing — Sprint 16
  invoiceMarkedPaid(tenantId: string, invoiceId: string): void {
    captureEvent('invoice_marked_paid', { tenantId, invoiceId });
  },
  invoiceCancelled(tenantId: string, invoiceId: string, reason: string): void {
    captureEvent('invoice_cancelled', {
      tenantId,
      invoiceId,
      reason: reason.slice(0, MAX_REASON_LEN),
    });
  },
  invoiceEmailResent(tenantId: string, invoiceId: string): void {
    captureEvent('invoice_email_resent', { tenantId, invoiceId });
  },
  billingSettingsUpdated(fields: string[]): void {
    captureEvent('billing_settings_updated', { fields });
  },

  // BI (Sprint 4.9+) — eventos de uso dos dashboards super-admin.
  biAlertAcknowledged(alertKind: string, ageDays: number): void {
    captureEvent('bi_alert_acknowledged', { alertKind, ageDays });
  },
  biCsvExported(source: string, rows: number): void {
    captureEvent('bi_csv_exported', { source, rows });
  },
  biPdfExported(reportMonth: string): void {
    captureEvent('bi_pdf_exported', { reportMonth });
  },
  // Bug corrigido em 2026-10-13 18:23 +0200 (#939, item 11): o painel de
  // origem ia na chave `surface`, a mesma da super property
  // `surface: 'web-admin'` (`lib/posthog.ts`) — a do evento prevalece, e este
  // evento saía sem a marca do admin. Vai como `dashboard`.
  biPostHogDeeplinkOpened(dashboard: string): void {
    captureEvent('bi_posthog_deeplink_opened', { dashboard });
  },

  // Web push (notificações do navegador) -------------------------------
  pushPermissionPrompted(): void {
    captureEvent('push_permission_prompted', { platform: 'web-admin' });
  },
  pushEnabled(): void {
    captureEvent('push_enabled', { platform: 'web-admin' });
  },
  pushDisabled(): void {
    captureEvent('push_disabled', { platform: 'web-admin' });
  },
  pushBlocked(): void {
    captureEvent('push_blocked', { platform: 'web-admin' });
  },
} as const;

/** Teto de caracteres de `reason` livre enviado ao PostHog. */
const MAX_REASON_LEN = 100;
